import * as React from 'react';
import CollapsibleCard from '../../../../common/CollapsibleCard';
import FormInput from '../../../../common/form/Input';
import Button from '../../../../common/form/Button';
import { addBrivoToken, BrivoPropertyConfiguration, BrivoToken, BrivoGroupIds, BrivoSiteIds, getAllBrivoGroups, getAllBrivoSites } from '../../../../../api/PropertyIntegration';
import { brivoConfig } from '../../../../../api/Shared';
import { useEffect, useState } from 'react';
import BrivoRefreshTokenErrorPopup from '../../BrivoRefreshTokenErrorPopup';
import FormSelectLabeled from '../../../../common/form/SelectLabeled';	
import { userIsCompanyManageUser, userIsPropertyManageUser, userIsSystemManageUser } from '../../../../../utilities/identity';																  
import MinimalMultiSelect from '../../../../common/MinimalMultiSelect';

interface Props {
    config?: BrivoPropertyConfiguration;
    id: number;
    currentCompanyId: number;
    reload: () => Promise<void>;
    update: (newConfig?: BrivoPropertyConfiguration) => void;
    setIsLoading: (isLoading: boolean) => void;
    isFormSubmitted: boolean;
    setIsFormSubmitted: any;
    previousBrivoConfiguration?: BrivoPropertyConfiguration;
}

const BrivoConfigurationSection = (props: Props) => {
    const isUser =
    userIsCompanyManageUser() ||
    userIsPropertyManageUser() ||
    userIsSystemManageUser();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(
        props.config && props.config.verified ? props.config.verified : false
    );
	const [siteList, setSiteList] = useState<BrivoSiteIds[]>([]);
    const [groupList, setGroupList] = useState<BrivoGroupIds[]>([]);
    // const [destinationSiteId, setDestinationSiteId] = useState<number[] | undefined>(undefined);
    const [destinationGroupId, setDestinationGroupId] = useState<number | undefined>(undefined);
    const [groupIdRequired, setGroupIdRequired] = useState<boolean>(false);
    const [siteIdRequired, setSiteIdRequired] = useState<boolean>(false);
    const [isCardOpen, setIsCardOpen] = useState<boolean>(!!props.config);
	const [removeCode, setRemoveCode] = useState(false);
	const isFormValid = (): boolean => {
        const { config } = props;
        return (
            config !== undefined &&
            Boolean(props.config && props.config.apiKey ? props.config.apiKey : '')  
       );
    };

    useEffect(() => {
        setGroupIdRequired(isAuthorized);
        setSiteIdRequired(isAuthorized);
    }, [isAuthorized]);					 
										 												   
    const handleAuthorize = async () => {
        const apiKey = props.config && props.config.apiKey ? props.config.apiKey : '';
        const clientId = props.config && props.config.clientId ? props.config.clientId : '';
        const state = `${props.currentCompanyId}-${props.id}`;
        //const redirectUri = `http://localhost:3000/admin/manage`; //For local
        const redirectUri = brivoConfig.redirectUri;

        if ( !apiKey ) {
            console.error('API Key is required');
            setErrorMessage('API Key is required');
            setShowErrorModal(true);
            return;
        }

        const extAuthUrl = `?response_type=code&client_id=${clientId}&api_key=${apiKey}&state=${state}&redirect_uri=${redirectUri}`;
        const authUrl = `${brivoConfig.authUrl}${extAuthUrl}`;

        //const authUrl = `https://auth.brivo.com/oauth/authorize?response_type=code&client_id=${clientId}&api_key=${apiKey}&state=${state}&redirect_uri=${redirectUri}`;
        window.location.href = authUrl;
        if (isCardOpen) {
            fetchGroupsAndSites();
        }					  
    };
         
		 const fetchGroupsAndSites = async () => {
            const propertyId = props.id;
            try {
                const [groupsData, sitesData] = await Promise.all([
                    getAllBrivoGroups(propertyId),
                    getAllBrivoSites(propertyId),
                ]);
    
                setGroupList(groupsData.data || []);
                setSiteList(sitesData.data || []);
            } catch (error) {
                console.error('Error fetching groups and sites:', error);
                setErrorMessage('Failed to load Brivo group and site data.');
                setShowErrorModal(true);
            }
        };

        useEffect(() => {
            if (props.config && isCardOpen) {
                const savedGroupId = props.config.groupId;
                const savedSiteId = props.config.siteIds;
        
                if (savedGroupId && savedSiteId) {
                    fetchGroupsAndSites().then(() => {
                        setDestinationGroupId(savedGroupId);
                        // setDestinationSiteId(savedSiteId);
                    });
                } else {

                    fetchGroupsAndSites();
                }
            }
        }, [props.config, isCardOpen]);
												   	
    const brivoConfigChange = React.useMemo(() => {
        const { config, previousBrivoConfiguration } = props;

        if (!config || !previousBrivoConfiguration) {
            return false;
        }

        console.log(config, previousBrivoConfiguration);
        const currentApiKey = config.apiKey ? config.apiKey : '';

        // Tracking the initial or previous values of the form inputs
        const previousApiKey = previousBrivoConfiguration.apiKey ? previousBrivoConfiguration.apiKey : '';

        // Compare the current form input values with the previous values
        const isChanged = (
            currentApiKey !== previousApiKey
        );

        return isChanged;
    }, [props.config, props.previousBrivoConfiguration])

    const exchangeCodeForToken = async (code: string, propertyId: number) => {

        try {

             const brivoToken: BrivoToken = {
                 propertyId: propertyId,
                 authcode: code
             };
             const addResponse = await addBrivoToken(brivoToken);
 
             if (addResponse.success) {
															   
                console.log('Token added successfully.');
                setRemoveCode(true)
				fetchGroupsAndSites();
                await props.reload();
             } else {
                 console.error('Failed to save the token. Please try again.');
                 setErrorMessage('Failed to save the token. Please try again.');
                 setShowErrorModal(true);
             }
        }
        catch (error) {
            console.error('Error exchanging code for token:', error);
            setErrorMessage('Error exchanging code for token. Please try again.');
            setShowErrorModal(true);
        }
    };

    const isValidCode = (code: string | null): boolean => {
        return code !== null && code.trim() !== '';
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const code: string | null = searchParams.get('code');
        const stateParam = searchParams.get('state');

        if (stateParam) {
            const [companyIdStr, propertyIdStr] = stateParam.split('-');

            const companyId = parseInt(companyIdStr, 10);
            const propertyId = parseInt(propertyIdStr, 10);
            console.log('Code: ', code);
            console.log('CompanyId: ', companyId);
            console.log('PropertyId: ', propertyId);

            if (isValidCode(code) && stateParam) {
                exchangeCodeForToken(code!, propertyId);
            } else if(code != null){
                setErrorMessage('Authcode is missing. Please check for a valid authcode.');
                setShowErrorModal(true);
            }
        }
    }, [location.search]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
    
        if (removeCode && code) {
            searchParams.delete('code');
            
            const newSearch = searchParams.toString();
            const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
            window.history.replaceState(null, '', newUrl);
        }
    }, [removeCode, location.search]);

    useEffect(() => {
        setIsAuthorized(props.config && props.config.verified ? props.config.verified : false);
    }, [props.config ? props.config.verified : false]);

    return (
        <>
            {showErrorModal && (
                <BrivoRefreshTokenErrorPopup
                    isOpen={showErrorModal}
                    message={errorMessage!}
                    onClose={() => setShowErrorModal(false)}
                />
            )}

            <CollapsibleCard
                cardTitle="Brivo Configuration"
                cardOpen={!!props.config}
                onCardOpenChange={(open) => {
                    if (isUser)  return;
                    setIsCardOpen(open);
                    if (!open) {
                        setGroupList([]);
                        setSiteList([]);
                        setDestinationGroupId(undefined);
                        // setDestinationSiteId(undefined);
                    }
                    props.setIsFormSubmitted(false);
                    props.update(open ? { clientId: '', clinetSecret: '', siteIds: [], apiKey: '', groupId: 0, verified: false } : undefined);
                }}
            >
                 {props.config && isCardOpen ? (
                    <div>
                        <FormInput
                            labelProps={{
                                labelText: 'API Key',
                                invalidText: 'Please enter a valid API Key',
                            }}
                            inputProps={{
                                id: 'api-key',
                                name: 'apiKey',
                                value: props.config.apiKey,
                                type: 'password',
                                required: true,
                                disabled: isUser
                            }}
                            onChange={(_name, value) =>
                                !isUser && props.update({
                                    ...props.config!,
                                    apiKey: value.toString(),
                                })
                            }
                        />
                        <label id="site-id-label" style={{ fontWeight: 'bold', fontSize: '10px' }}>Site ID</label>
                        { siteList && siteList.length>0 && <MinimalMultiSelect 
                           heading={(props.config.siteIds && props.config.siteIds.length>0)?  props.config.siteIds.length + ( props.config.siteIds.length ===1? ' Site Selected': ' Sites Selected'): 'Select Sites'} 
                           listItems={siteList} 
                           selectedIds={props.config.siteIds!=undefined?props.config.siteIds:[]}
                           setSelectedIds={function (ids: any[]): void {
                            // const selectedSiteIds = siteList.filter((site: any) => ids.includes(site.id));
                            props.update({
                                ...props.config!,
                                siteIds: ids,  
                            });
                           }}
                        />
                        }
                        {siteIdRequired && siteList.length===0 &&<p style={{ color: 'red' }}>Please select a valid Site IDs</p>}
                        <FormSelectLabeled
                            selectProps={{
                                id: 'group-id',
                                name: 'groupId',
                                value: destinationGroupId,
                                required: groupIdRequired,
                                disabled: isUser
                            }}
                            labelProps={{
                                labelText: 'Group ID',
                                invalidText: groupIdRequired ? 'Please select a valid Group ID' : undefined,		   
                            }}
                            onChange={(_name, value) => {
                                setDestinationGroupId(Number(value));  
                                !isUser && props.update({
                                    ...props.config!,
                                    groupId: Number(value),  
                                });
                            }}
                            selectOptions={groupList.map((group) => ({ label: group.name, value: group.id }))}
                        />
                        {/* Buttons for Authorize and Refresh Token */}
                        <div className="d-flex align-items-center">
                            <Button
                                text="Authorize with Brivo"
                                onClick={handleAuthorize}
                                addlClassName="btn-primary"
                                disabled={!isFormValid() || brivoConfigChange || !props.isFormSubmitted || isAuthorized}
                            />
                            {isAuthorized && (
                                <span className="ml-2 text-success">✓ Authorized</span>
                            )}
                        </div>
                        {isFormValid() || brivoConfigChange || !props.isFormSubmitted && (
                            <div className="mt-2 text-muted">
                                Add Brivo configuration details and save to enable "Authorize with Brivo"
                            </div>
                        )}
                        {brivoConfigChange && (
                            <div className="mt-2 text-muted">
                                Brivo configurations have been modified. Please save the changes to enable "Authorize with Brivo"
                            </div>
                        )}
                    </div>
                ) : (
                    <div />
                )}
            </CollapsibleCard>
        </>
    );
};

export default BrivoConfigurationSection;



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/integrationControls/BrivoConfigurationSection.tsx